import { ProgramID } from "@/api/generated/models";
import type { UseCase } from "@/hooks/suggestions";
import type { Agent } from "@/types/agent";

const VALID_CONDITIONS = ["weight-loss", "menopause"] as const;
type RxCondition = (typeof VALID_CONDITIONS)[number];

const MAX_MESSAGE_LENGTH = 2000;

export type EntryParams =
  | {
      action: "message";
      msg: string;
      agent?: Agent;
      useCase?: UseCase;
      sourceConversationId?: string;
    }
  | { action: "rx-eligibility"; condition: RxCondition }
  | { action: "rx_renewal" }
  | { action: "diagnosis" }
  | { action: "program"; program: ProgramID };

export type ParseResult = {
  params?: EntryParams;
  utmTerm?: string;
  error?: string;
};

export function parseEntryParams(
  searchParams: Record<string, string | undefined>,
): ParseResult {
  const action = searchParams.action;
  const utmTerm = searchParams.utm_term;

  if (action === "message") {
    const msg = searchParams.msg?.trim();
    if (!msg) {
      return { error: "Missing required 'msg' parameter for message action" };
    }
    if (msg.length > MAX_MESSAGE_LENGTH) {
      return { error: `Message exceeds max length of ${MAX_MESSAGE_LENGTH}` };
    }

    return {
      params: {
        action: "message",
        msg,
        agent: searchParams.agent as Agent | undefined,
        useCase: searchParams.useCase as UseCase | undefined,
        sourceConversationId: searchParams.source_conversation_id,
      },
      utmTerm,
    };
  }

  if (action === "rx-eligibility") {
    const condition = searchParams.condition;
    if (!condition || !VALID_CONDITIONS.includes(condition as RxCondition)) {
      return {
        error: `Invalid condition '${condition}'. Must be one of: ${VALID_CONDITIONS.join(", ")}`,
      };
    }
    return {
      params: { action: "rx-eligibility", condition: condition as RxCondition },
      utmTerm,
    };
  }

  if (action === "rx_renewal" || action === "diagnosis") {
    return { params: { action }, utmTerm };
  }

  if (action === "program") {
    const program = searchParams.program;
    const valid = Object.values(ProgramID) as string[];
    if (!program || !valid.includes(program)) {
      return {
        error: `Invalid program '${program}'. Must be one of: ${valid.join(", ")}`,
      };
    }
    return {
      params: { action: "program", program: program as ProgramID },
      utmTerm,
    };
  }

  return {
    error: `Invalid action '${action}'. Must be one of: message, rx-eligibility, rx_renewal, diagnosis, program`,
  };
}
